import { useState, useEffect } from 'react';
import queryString from 'query-string';

import { navigate } from 'gatsby';
import authServiceInvoicePortal from '../../lib/authServiceInvoicePortal';
import {
  clearSessionAndLocalStorage,
  getFromStorage,
  removeFromStorage,
  setInStorage,
} from '../../util/storage-utils';
import routes from '../../routes';
import { useLocation } from '@reach/router';
import useContentMgmt from '../useContentMgmt';
import useFormState from '../useFormState';
import SignInValidationRules, {
  Model,
  createPasswordResetValidationFunction,
} from '../../components/invoice-payment-portal/auth/SignInValidation.rules';
import useTransition from '../useTransition';
import { AuthState } from '../../lib/AuthenticationService/types';
import microcopyGroupIds from '../../components/pge-plus-common/microcopyGroupIds';
import useUtil from './useUtil';
import apolloClient from '../../lib/apolloClient';
import {
  AUTH_ERROR,
  AuthError,
  AuthErrorCode,
  createAuthError,
} from '../../lib/AuthenticationService/errorCodes';

type QueryParams = {
  jobRequest?: string;
  continueUrl?: string;
  lang?: string;
};

const invoicePaymentEmailKey = 'invoicePaymentEmail';

export const INVOICE_PAYMENT_SIGNIN_EMAIL_ID_STORAGE_KEY =
  'emailForInvoicePaymentSignIn';

const useInvoicePaymentAuth = () => {
  const [authState, setState] = useState<AuthState>(
    authServiceInvoicePortal.getState(),
  );
  const { content } = useContentMgmt(microcopyGroupIds.INVOICE_PAYMENT_SIGN_IN);

  const signInWithCustomToken = authServiceInvoicePortal.signInWithCustomToken.bind(
    authServiceInvoicePortal,
  );

  const changePassword = authServiceInvoicePortal.changePassword.bind(
    authServiceInvoicePortal,
  );

  const forgotPassword = authServiceInvoicePortal.forgotPassword.bind(
    authServiceInvoicePortal,
  );

  const verifyAndResetPassword = authServiceInvoicePortal.verifyAndResetPassword.bind(
    authServiceInvoicePortal,
  );
  const { hideTransition, showTransition } = useTransition();

  const location = useLocation();

  const defaultEmail: string | undefined = getFromStorage(
    invoicePaymentEmailKey,
  );

  const form = useFormState(
    {
      email: defaultEmail || '',
      password: '',
      rememberMe: true,
    },
    {
      validate: SignInValidationRules,
      validationContext: {
        content,
      },
    },
  );

  const passwordResetForm = useFormState(
    {
      email: '',
      password: '',
      confirmPassword: '',
    },
    {
      validate: createPasswordResetValidationFunction,
      validationContext: {
        content,
      },
    },
  );

  useEffect(() => {
    return authServiceInvoicePortal.subscribe(setState);
  }, []);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChangePassword, setIsChangePassword] = useState<boolean>(false);

  const { setErrorNotification } = useUtil();
  const queryParams = queryString.parse(location.search) as QueryParams;

  const handleRedirect = () => {
    if (queryParams.continueUrl) {
      void navigate(
        queryParams.continueUrl.replace(window.location.origin, ''),
      );
    } else {
      void navigate(routes.INVOICE_PAYMENT_PORTAL_INVOICE_LIST);
    }
  };

  //const signOut = authServiceInvoicePortal.signOut.bind(authServiceInvoicePortal);
  const handleSignOut = async () => {
    // Queries need to be stopped before the store is cleared, if a query is in-flight,
    // an error will be returned and we will respond with an error page
    //stopAllQueries();
    await apolloClient.stop();
    await apolloClient.clearStore();
    clearSessionAndLocalStorage();
    authServiceInvoicePortal.signOut();
  };

  const signOut = async () => {
    await handleSignOut();
    await navigate(routes.INVOICE_PAYMENT_PORTAL_SIGN_IN);
  };

  const refreshTokens = authServiceInvoicePortal.refreshTokens.bind(
    authServiceInvoicePortal,
  );
  const startTokenRefresher = authServiceInvoicePortal.startTokenRefresher.bind(
    authServiceInvoicePortal,
  );
  const stopTokenRefresher = authServiceInvoicePortal.stopTokenRefresher.bind(
    authServiceInvoicePortal,
  );

  const handleLogin = async (data: Model) => {
    showTransition(content.get('SUBMITTING_REQUEST') || '');
    try {
      handleRememberMe(Boolean(data.rememberMe), data.email);
      const result = await authServiceInvoicePortal.signInWithPassword(
        data.email,
        data.password,
      );
      if (result.status === 'SUCCESS') {
        handleRedirect();
        hideTransition();
        return;
      }
      if (result.status === 'CHPS_REQUIRED') {
        setIsChangePassword(true);
      }
      form.setError('password', content.get('ERROR_AUTH_UNEXPECTED'));
      hideTransition();
    } catch (err) {
      const authError = err instanceof AuthError ? err : createAuthError(err);
      showErrorMessage(authError);
      hideTransition();
    }
  };

  const showErrorMessage = (error: Error): void => {
    const code = error instanceof AuthError ? error.code : error.message;
    switch (code) {
      case AUTH_ERROR.EMAIL_NOT_FOUND:
        form.setError('email', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AUTH_ERROR.INVALID_PASSWORD:
        form.setError('password', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AUTH_ERROR.TOO_MANY_ATTEMPTS:
        form.setError('password', content.get('ERROR_AUTH_EXCEEDED_ATTEMPTS'));
        break;

      default:
        form.setError('password', content.get('ERROR_AUTH_UNEXPECTED'));
    }
  };

  const handleRememberMe = (rememberMe: boolean, email: string): void => {
    if (rememberMe) {
      setInStorage(invoicePaymentEmailKey, email);
    } else {
      removeFromStorage(invoicePaymentEmailKey);
    }
  };

  const handleLoginWithPassword = form.submit(handleLogin);

  return {
    ...authState,
    form,
    passwordResetForm,
    isLoading,
    authUser: { email: getFromStorage(invoicePaymentEmailKey) },
    errorMessage,
    signOut,
    startTokenRefresher,
    stopTokenRefresher,
    refreshTokens,
    setErrorMessage,
    handleLoginWithPassword,
    signInWithCustomToken,
    isChangePassword,
    setIsChangePassword,
    changePassword,
    handleRedirect,
    forgotPassword,
    verifyAndResetPassword,
  };
};

export default useInvoicePaymentAuth;
